import { ActionBarPrimitive, MessagePrimitive } from '@assistant-ui/react';
import { Button } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';

import { ErrorFallback } from '#core/components/error/ErrorFallback.tsx';
import { useKeyedRegistry } from '#core/hooks/useKeyedRegistry.ts';

import { AssistantActionBar } from './AssistantActionBar.tsx';
import { BranchPicker } from './BranchPicker.tsx';
import { CollapsibleMessageParts } from './CollapsibleMessageParts.tsx';
import classes from './Thread.module.css';
import { Bubble } from '#plugin-chat/components/bubble/Bubble.tsx';
import { type MessageDashboardEntry, MessageExportContext } from '#plugin-chat/contexts/MessageExportContext.ts';

/**
 * Renders an assistant message with tool displays, error handling, and branch navigation.
 * Dynamically renders message parts (text, tool calls) using assistant-ui primitives.
 */
export function AssistantMessage() {
  // Per-message dashboard export registry — each `<DashboardSpecRenderer>`
  // rendered by a tool inside this message registers its tile snapshot.
  // The action bar reads the registry to drive its "download all" button.
  const messageExport = useKeyedRegistry<MessageDashboardEntry>();

  return (
    <MessagePrimitive.Root className={classes.messageContainer} data-role="assistant">
      <div className={classes.contentWrapper}>
        <CollapsibleMessageParts key="message-parts" />

        <MessagePrimitive.Error key="message-error">
          <Bubble kind="error" role="assistant">
            <ErrorFallback
              error="The assistant couldn't complete your request. You can try again or rephrase your message."
              retryAction={
                <ActionBarPrimitive.Reload asChild>
                  <Button color="red" leftSection={<IconRefresh size={14} />} size="xs" variant="light">
                    Retry
                  </Button>
                </ActionBarPrimitive.Reload>
              }
              showDetails={false}
              showRetry
              title="Something went wrong"
              variant="alert"
            />
          </Bubble>
        </MessagePrimitive.Error>
      </div>
      <div className={classes.assistantFooter}>
        <BranchPicker />
        <MessageExportContext value={messageExport}>
          <AssistantActionBar />
        </MessageExportContext>
      </div>
    </MessagePrimitive.Root>
  );
}
